import { useTranslation } from 'react-i18next';
import type { Screen } from '@ghostscreen/contracts';
import { formatRelativeTime } from '../../../lib/dashboard/relativeTime';

type Props = {
  lastPlayed?: Screen['lastPlayed'];
  lastFailure?: Screen['lastFailure'];
  /** For tests. */
  now?: number;
};

/**
 * What the screen itself reported playing (proof-of-play), kept apart from what is assigned.
 * Both fields can be missing (older API) or null (nothing reported yet): never invent values.
 */
export function DisplayPlaybackProof({ lastPlayed, lastFailure, now }: Props) {
  const { t } = useTranslation('displays');
  const name = (assetName: string | null) => assetName ?? t('playbackProof.unknownAsset');
  const failureDetail = lastFailure?.errorMessage ?? lastFailure?.errorCode ?? null;

  return (
    <div className="flex flex-col gap-1 px-1 text-[12.5px] text-muted" data-testid="display-playback-proof">
      <span data-testid="display-last-played">
        <span className="font-semibold text-ink">{t('playbackProof.lastPlayed')}:</span>{' '}
        {lastPlayed
          ? `${name(lastPlayed.mediaAssetName)} · ${formatRelativeTime(lastPlayed.playedAt, now)}`
          : t('playbackProof.none')}
      </span>
      {lastFailure ? (
        <span data-testid="display-last-failure" style={{ color: 'var(--gs-danger, #d14343)' }}>
          <span className="font-semibold">{t('playbackProof.lastFailure')}:</span>{' '}
          {`${name(lastFailure.mediaAssetName)} · ${formatRelativeTime(lastFailure.playedAt, now)}`}
          {failureDetail ? ` · ${failureDetail}` : ''}
        </span>
      ) : null}
    </div>
  );
}
